import { useState, useMemo, useCallback, useEffect } from 'react';

export const useDynamicSelectPlacement = (fieldNames = []) => {
  const [menuPlacements, setMenuPlacements] = useState({});
  const [openMenus, setOpenMenus] = useState(new Set());
  const fieldNamesKey = fieldNames?.join(',');
  /* eslint-disable react-hooks/exhaustive-deps */
  const filterRefs = useMemo(() => {
    const refs = {};
    fieldNames?.forEach(fieldName => {
      refs[fieldName] = { current: null };
    });
    return refs;
  }, [fieldNamesKey, fieldNames]);
  /* eslint-disable react-hooks/exhaustive-deps */

  const getMenuPlacement = useCallback((fieldName) => {
    const ref = filterRefs[fieldName];
    if (!ref?.current) return "auto";
    
    const rect = ref.current.getBoundingClientRect();
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    const minSpaceNeeded = 200;
    
    if (spaceBelow < minSpaceNeeded && spaceAbove > spaceBelow) {
      return "top";
    }
    return "bottom";
  }, [filterRefs]);

  const handleMenuOpen = useCallback((fieldName) => {
    const placement = getMenuPlacement(fieldName);
    setMenuPlacements(prev => ({
      ...prev,
      [fieldName]: placement
    }));
    setOpenMenus(prev => new Set(prev).add(fieldName));
  }, [getMenuPlacement]);

  
  useEffect(() => {
    if (openMenus?.size === 0) return;

    let rafId = null;
    let isScrolling = false;

    const updatePlacements = () => {
      openMenus?.forEach(fieldName => {
        const placement = getMenuPlacement(fieldName);
        setMenuPlacements(prev => {
          if (prev[fieldName] !== placement) {
            return {
              ...prev,
              [fieldName]: placement
            };
          }
          return prev;
        });
      });
      isScrolling = false;
    };

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        if (rafId) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(updatePlacements);
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('scroll', handleScroll, true);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [openMenus, getMenuPlacement]);

 
  useEffect(() => {
    if (openMenus?.size === 0) return;

    let resizeTimeout;

    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        openMenus?.forEach(fieldName => {
          const placement = getMenuPlacement(fieldName);
          setMenuPlacements(prev => ({
            ...prev,
            [fieldName]: placement
          }));
        });
      }, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [openMenus, getMenuPlacement]);

  return {
    filterRefs,
    menuPlacements,
    handleMenuOpen,
  };
};

export const getSelectProps = (fieldName, { menuPlacements, handleMenuOpen}) => ({
  menuPlacement: menuPlacements[fieldName] || "auto",
  onMenuOpen: () => handleMenuOpen(fieldName),
});

